import { useEffect, useMemo, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Dropdown,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Radio,
  Stack,
  styled,
  Typography,
  useColorScheme,
} from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import CalyxLogo from 'jsx:../../assets/icons/logo.svg';
import CalyxLogoDark from 'jsx:../../assets/icons/logoDark.svg';
import { BiLock } from 'react-icons/bi';
import { FiPlusCircle } from 'react-icons/fi';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { IoMenu } from 'react-icons/io5';
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { usePermissionsContext } from '../../context/permissionsContext';
import { track } from '../../services/analytics';
import {
  EVENT_DASHBOARD_PAGE,
  EVNTAPP_contact_menu_click,
  EVNTAPP_myprofile_menu_click,
  EVNTAPP_programs_menu_click,
  EVNTAPP_retiree_insights_menu_click,
  EVNTAPP_search_menu_click,
  EVNTAPP_signout_menu_click,
  EVNTAPP_research_hub_menu_click,
  EVNT_nav_upgrade_clicked,
  EVNTAPP_following_menu_click,
} from '../../services/analytics/events';
import { makeAuthenticatedGetRequest } from '../../services/axios';
import { getFollowedProjectsCount } from '../../services/axios/endpoints';
import { appState } from '../../stores/app';
import { miscState } from '../../stores/misc';
import { userState } from '../../stores/user';
import useIsCalyxTeamMember from '../../utils/hooks/useIsCalyxTeamMember';
import useIsMobile from '../../utils/hooks/useIsMobile';
import { useLogout } from '../../utils/hooks/useLogout';
import { usePermissions } from '../../utils/hooks/usePermissions';
import usePreviousRoute from '../../utils/hooks/usePreviousRoute';

import { projectDetailRoutes } from './constants';

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  borderRadius: theme.radius.xl,
  height: '44px',
  padding: 1,
  border: `1px solid transparent`,
  '&:hover': {
    backgroundColor: theme.palette.primary.outlinedHoverBg,
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledNavBox = styled(Box)<{ active?: boolean }>(({ theme, active }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(1)} ${theme.spacing(3)}`,
  backgroundColor: active ? theme.palette.primary.outlinedHoverBg : '',
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  background: 'transparent !important',
  ':active': {
    border: 'none',
  },
  '>button': {
    background: 'transparent !important',
    justifyContent: 'start',
  },
});

const FOLLOWING_PROJECTS_ON_SEARCH_PAGE = '/credit-ratings?following=true';

interface NavItem {
  path: string;
  title: string;
  isNew?: boolean;
  isDropdown?: boolean;
  items?: Array<NavItem>;
  isBeta?: boolean;
  event: string;
  hasPermission?: boolean;
  show: boolean;
  activeCallback?: (path: string) => boolean;
}

const Navbar = () => {
  const { hasPermission } = usePermissions();
  const { isFreemiumUser, isVersionMismatch, isDemoUser } = usePermissionsContext();
  const hasPermissionForResearchHub = hasPermission('research_hub:research_list');
  const hasProgramsListPermission = hasPermission('programs:list');
  const hasRetirementInsightsPermission = hasPermission('retirement_insights:list');
  const userInfo = useRecoilValue(userState);
  const [isHoveringOverInsights, setIsHoveringOverInsights] = useState<boolean>(false);
  const [isHoveringOverProfile, setIsHoveringOverProfile] = useState<boolean>(false);
  const logout = useLogout();
  const { mode, setMode } = useColorScheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const appStateData = useRecoilValue(appState);
  const setAppState = useSetRecoilState(appState);
  const setMiscState = useSetRecoilState(miscState);
  const isCalyxTeamMember = useIsCalyxTeamMember();
  const isMobile = useIsMobile();
  const prevPath = usePreviousRoute();
  const { filterState } = useRecoilValue(miscState);
  const isProjectDeveloper = useMemo(() => userInfo?.company_type === 'developer', [userInfo]);

  const navItemsConfig: Array<NavItem> = [
    {
      title: 'Credit ratings',
      path: '/credit-ratings',
      isBeta: false,
      activeCallback: (path) => !!projectDetailRoutes.includes(path),
      event: EVNTAPP_search_menu_click,
      show: true,
    },
    {
      title: 'Early stage',
      path: '/compass',
      isBeta: false,
      show: true, // Now available to all users
      event: '',
    },
    {
      title: 'Research hub',
      path: '/research-hub',
      event: EVNTAPP_research_hub_menu_click,
      hasPermission: hasPermissionForResearchHub,
      show: true,
    },
    {
      title: 'Retirement insights',
      path: '/retirement_insights',
      event: EVNTAPP_retiree_insights_menu_click,
      hasPermission: hasRetirementInsightsPermission,
      show: true,
    },
    {
      title: 'Developer portal',
      path: '/developer-portal',
      event: '',
      show: isProjectDeveloper,
    },
    {
      title: 'Programs',
      path: '/programs',
      event: EVNTAPP_programs_menu_click,
      hasPermission: hasProgramsListPermission,
      show: true,
    },
  ];

  const profileMenuConfig = [
    {
      title: 'Settings',
      show: true,
      onClick: () => {
        track(EVNTAPP_myprofile_menu_click, EVENT_DASHBOARD_PAGE);
        window.open(
          process.env.IS_PRODUCTION_APP
            ? 'https://auth.calyxglobal.com/account'
            : 'https://auth.calyxstaging.com/account',
          '_blank'
        );
      },
    },
    {
      title: 'Developer Portal',
      show: !isProjectDeveloper && isFreemiumUser,
      onClick: () => {
        navigate('/developer-portal');
      },
    },
    {
      title: 'Contact Us',
      show: !isFreemiumUser,
      onClick: () => {
        track(EVNTAPP_contact_menu_click, EVENT_DASHBOARD_PAGE);
        navigate('/contact', { state: { entryPoint: 'top nav menu' } });
      },
    },
    {
      title: 'Log Out',
      show: true,
      onClick: () => {
        track(EVNTAPP_signout_menu_click, EVENT_DASHBOARD_PAGE);
        logout();
      },
    },
  ];

  const themeOptionsList: Array<{ label: string; value: 'light' | 'dark' }> = [
    {
      label: 'Light Mode',
      value: 'light',
    },
    {
      label: 'Dark Mode',
      value: 'dark',
    },
  ];

  const envOptionsList: Array<{ label: string; value: boolean }> = [
    {
      label: 'Production',
      value: false,
    },
    {
      label: 'Staging',
      value: true,
    },
  ];

  const handleNavItemClick = (event: string) => {
    track(event, EVENT_DASHBOARD_PAGE);
  };

  const handleThemeSwitch = (mode: 'dark' | 'light') => {
    setMode(mode);
  };

  const { data: followingProjectsCount, isLoading } = useQuery({
    queryKey: [`followed-projects-count`],
    queryFn: async () => await makeAuthenticatedGetRequest(getFollowedProjectsCount),
    select: (data) => data?.data?.count,
  });

  useEffect(() => {
    setMiscState((state) => ({ ...state, prevPath: prevPath || '' }));
  }, [prevPath]);

  const handleEnvToggle = () => {
    setAppState({ isStaging: !appStateData.isStaging });
    window.location.reload();
  };

  const handleUpgradeClick = () => {
    track(EVNT_nav_upgrade_clicked, EVENT_DASHBOARD_PAGE);
    navigate('/upgrade');
  };

  const handleFollowingClick = () => {
    track(EVNTAPP_following_menu_click, EVENT_DASHBOARD_PAGE);
  };

  return (
    <Stack
      sx={{
        position: 'fixed',
        width: 1,
        top: 0,
        zIndex: 11,
        background: (theme) => theme.palette.background.surface,
        borderBottom: (theme) => `1px solid ${theme.palette.primary.outlinedBorder}`,
      }}
    >
      {isVersionMismatch && (
        <Alert variant='soft' color='warning' sx={{ py: 0 }}>
          <Typography fontSize={'sm'} fontWeight={'lg'} mx={'auto'}>
            Your app version is out of date. Please{' '}
            <Link to='#' onClick={() => window.location.reload()}>
              refresh
            </Link>{' '}
            to load the latest version.
          </Typography>
        </Alert>
      )}
      <Drawer
        open={isDrawerOpen}
        anchor='right'
        sx={{ display: { lg: 'none' } }}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Stack direction={'row'} justifyContent={'end'} sx={{ m: 1 }}>
          <IconButton variant='plain' onClick={() => setIsDrawerOpen(false)}>
            <IoMenu fontSize={20} />
          </IconButton>
        </Stack>
        {navItemsConfig.map(
          ({
            path,
            title,
            isNew,
            isDropdown = false,
            items = [],
            isBeta,
            event,
            show,
            activeCallback,
            hasPermission = true,
          }) => {
            if (!show) {
              return null;
            } else {
              return isDropdown ? (
                <Accordion variant='plain'>
                  <AccordionGroup disableDivider sx={{ maxWidth: 400 }}>
                    <StyledAccordionSummary sx={{ px: 3, py: 1 }}>{title}</StyledAccordionSummary>
                    <AccordionDetails>
                      {items.map(({ path, title, event: childEvent }) => {
                        return (
                          <Link
                            to={path}
                            style={{ textDecoration: 'none' }}
                            onClick={() => handleNavItemClick(childEvent)}
                          >
                            <StyledNavBox
                              active={
                                !!location.pathname.startsWith(path) ||
                                activeCallback?.(location.pathname)
                              }
                            >
                              <Typography fontSize={'md'} fontWeight={'md'}>
                                {title}
                              </Typography>
                            </StyledNavBox>
                          </Link>
                        );
                      })}
                    </AccordionDetails>
                  </AccordionGroup>
                </Accordion>
              ) : (
                <Link
                  to={path}
                  style={{ textDecoration: 'none' }}
                  onClick={() => handleNavItemClick(event)}
                >
                  <StyledNavBox
                    active={
                      !!location.pathname.startsWith(path) || activeCallback?.(location.pathname)
                    }
                  >
                    <Typography
                      startDecorator={hasPermission ? null : <BiLock />}
                      endDecorator={
                        isNew || isBeta ? (
                          <>
                            {isNew ? (
                              <Chip variant='solid' color='primary'>
                                New
                              </Chip>
                            ) : null}
                            {isBeta ? (
                              <Chip variant='solid' color='primary'>
                                Beta
                              </Chip>
                            ) : null}
                          </>
                        ) : null
                      }
                    >
                      {title}
                    </Typography>
                  </StyledNavBox>
                </Link>
              );
            }
          }
        )}
        {/* account menu mobile view */}
        <Stack px={3} py={1}>
          <Divider />
          <FormControl sx={{ my: 1 }}>
            <FormLabel sx={{ fontWeight: 'xl', fontSize: 'sm' }}>Appearance</FormLabel>
            {themeOptionsList.map(({ label, value }) => {
              return (
                <Stack
                  direction={'row'}
                  gap={1}
                  alignItems={'center'}
                  onClick={() => handleThemeSwitch(value)}
                >
                  <Radio
                    value={value}
                    variant='outlined'
                    size='md'
                    color='primary'
                    sx={{ fontSize: 'md' }}
                    checked={mode === value}
                  />
                  <Typography fontSize={'md'} fontWeight={'sm'}>
                    {label}
                  </Typography>
                </Stack>
              );
            })}
          </FormControl>
          {isCalyxTeamMember ? (
            <FormControl sx={{ my: 1 }}>
              <FormLabel sx={{ fontWeight: 'xl', fontSize: 'sm' }}>App Environment</FormLabel>
              {envOptionsList.map(({ label, value }) => {
                return (
                  <Stack onClick={handleEnvToggle} direction={'row'} gap={1} alignItems={'center'}>
                    <Radio
                      value={value}
                      variant='outlined'
                      size='md'
                      color='primary'
                      sx={{ fontSize: 'md' }}
                      checked={appStateData.isStaging === value}
                    />
                    <Typography fontSize={'md'} fontWeight={'sm'}>
                      {label}
                    </Typography>
                  </Stack>
                );
              })}
            </FormControl>
          ) : null}
          <Divider />
          <Stack direction={'row'} py={1} onClick={handleFollowingClick}>
            <StyledLink
              to={`${FOLLOWING_PROJECTS_ON_SEARCH_PAGE}${filterState?.searchParams.has('view') ? `&view=${filterState?.searchParams.get('view')}` : ''}`}
              sx={{ height: (theme) => theme.spacing(4) }}
            >
              <Button
                size='md'
                variant='plain'
                color='primary'
                startDecorator={<FiPlusCircle size={20} />}
                sx={{ padding: 0 }}
              >
                <Typography fontSize={'md'} fontWeight={'sm'}>
                  Following ({isLoading ? '...' : followingProjectsCount})
                </Typography>
              </Button>
            </StyledLink>
          </Stack>
          {profileMenuConfig.map(({ onClick, title, show }) => {
            if (!show || title.toLowerCase() === 'log out') return null;
            return (
              <>
                <Divider />
                <Stack direction={'row'} py={1} onClick={onClick}>
                  <Typography fontSize={'md'} fontWeight={'sm'}>
                    {title}
                  </Typography>
                </Stack>
              </>
            );
          })}
          <Button
            variant='outlined'
            onClick={() => {
              track(EVNTAPP_signout_menu_click, EVENT_DASHBOARD_PAGE);
              logout();
            }}
            sx={{ mt: 2 }}
          >
            Log out
          </Button>
        </Stack>
      </Drawer>
      <Grid container justifyContent={'space-between'} alignItems={'center'} padding={'4px 24px'}>
        <Stack alignItems={'center'} direction={'row'} gap={3}>
          <Link to={'/'}>
            {mode === 'light' ? (
              <CalyxLogo width={164} height={36} />
            ) : (
              <CalyxLogoDark width={164} height={36} />
            )}
          </Link>
          <Grid
            container
            spacing={2}
            alignItems={'center'}
            sx={{ display: { xs: 'none', lg: 'flex' } }}
          >
            {navItemsConfig.map(
              ({
                path,
                title,
                isNew,
                isDropdown = false,
                items = [],
                isBeta = false,
                event,
                show,
                activeCallback,
                hasPermission = true,
              }) => {
                const isActive =
                  (isDropdown
                    ? items?.some(({ path }) => path === location.pathname)
                    : path === location.pathname || location.pathname.startsWith(path)) ||
                  activeCallback?.(location.pathname);

                if (!show) {
                  return null;
                }
                return isDropdown ? (
                  <Grid
                    padding={1}
                    sx={{
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => {
                      setIsHoveringOverInsights(true);
                    }}
                    onMouseLeave={() => {
                      setIsHoveringOverInsights(false);
                    }}
                  >
                    <Dropdown>
                      <Stack mt={'4px'}>
                        <MenuButton variant='plain' color='primary'>
                          <Typography
                            fontSize={'md'}
                            fontWeight={'md'}
                            color='primary'
                            endDecorator={
                              isHoveringOverInsights ? <IoChevronUp /> : <IoChevronDown />
                            }
                          >
                            {title}
                          </Typography>
                        </MenuButton>
                        {isActive ? (
                          <Box
                            width={'100%'}
                            position={'relative'}
                            top={10}
                            height={'4px'}
                            sx={(theme) => {
                              return { background: theme.palette.primary.solidBg };
                            }}
                          />
                        ) : (
                          <Box height={'4px'} />
                        )}
                      </Stack>
                      <Menu open={isHoveringOverInsights}>
                        {items.map(({ path, title, event: childEvent }) => {
                          return (
                            <Link
                              to={path}
                              style={{ textDecoration: 'none' }}
                              onClick={() => handleNavItemClick(childEvent)}
                            >
                              <MenuItem>
                                <Typography fontSize={'md'} fontWeight={'md'}>
                                  {title}
                                </Typography>
                              </MenuItem>
                            </Link>
                          );
                        })}
                      </Menu>
                    </Dropdown>
                  </Grid>
                ) : (
                  <StyledLink
                    to={path}
                    style={{ textDecoration: 'none' }}
                    onClick={() => handleNavItemClick(event)}
                  >
                    <Stack>
                      <Grid padding={1} sx={{ cursor: 'pointer' }} mt={'4px'}>
                        <Typography
                          fontSize={'md'}
                          fontWeight={'md'}
                          color='primary'
                          startDecorator={hasPermission ? null : <BiLock />}
                          endDecorator={
                            isNew || isBeta ? (
                              <>
                                {isNew ? (
                                  <Chip variant='solid' color='primary'>
                                    New
                                  </Chip>
                                ) : null}
                                {isBeta ? (
                                  <Chip variant='solid' color='primary'>
                                    Beta
                                  </Chip>
                                ) : null}
                              </>
                            ) : null
                          }
                        >
                          {title}
                        </Typography>
                      </Grid>
                      {isActive ? (
                        <Box
                          width={'100%'}
                          position={'relative'}
                          top={8}
                          height={'4px'}
                          sx={(theme) => {
                            return { background: theme.palette.primary.solidBg };
                          }}
                        />
                      ) : (
                        <Box height={'4px'} />
                      )}
                    </Stack>
                  </StyledLink>
                );
              }
            )}
          </Grid>
        </Stack>
        <Stack alignItems={'center'} gap={1} direction={'row'}>
          {(isFreemiumUser || isDemoUser) && (
            <Button variant='solid' color='primary' size='sm' onClick={handleUpgradeClick}>
              Upgrade
            </Button>
          )}
          {isMobile ? null : (
            <>
              <div
                onMouseEnter={() => {
                  setIsHoveringOverProfile(true);
                }}
                onMouseLeave={() => {
                  setIsHoveringOverProfile(false);
                }}
                style={{ padding: '3px 0px', margin: '-3px 0px' }}
              >
                <Dropdown>
                  <MenuButton variant='plain' color='primary'>
                    <Stack direction={'row'} alignItems={'center'} gap={1}>
                      <Avatar
                        alt={`${userInfo?.name}`}
                        src={`${userInfo?.profile_image}`}
                        variant='outlined'
                        color='primary'
                      />
                      <Typography fontSize={'md'} fontWeight={'md'} color='primary'>
                        My Account
                      </Typography>
                      {isHoveringOverProfile ? <IoChevronUp /> : <IoChevronDown />}
                    </Stack>
                  </MenuButton>
                  <Menu
                    variant='outlined'
                    sx={{
                      minWidth: '200px',
                    }}
                    open={isHoveringOverProfile}
                  >
                    <FormControl sx={{ mb: 1 }}>
                      <FormLabel sx={{ paddingX: 1, fontWeight: 'xl', fontSize: 'sm' }}>
                        Appearance
                      </FormLabel>
                      {themeOptionsList.map(({ label, value }) => {
                        return (
                          <MenuItem onClick={() => handleThemeSwitch(value)}>
                            <Radio
                              value={value}
                              variant='outlined'
                              size='md'
                              color='primary'
                              sx={{ fontSize: 'md' }}
                              checked={mode === value}
                            />
                            <Typography fontSize={'md'} fontWeight={'sm'}>
                              {label}
                            </Typography>
                          </MenuItem>
                        );
                      })}
                    </FormControl>
                    {isCalyxTeamMember ? (
                      <FormControl sx={{ mb: 1 }}>
                        <FormLabel sx={{ paddingX: 1, fontWeight: 'xl', fontSize: 'sm' }}>
                          App Environment
                        </FormLabel>
                        {envOptionsList.map(({ label, value }) => {
                          return (
                            <MenuItem onClick={handleEnvToggle}>
                              <Radio
                                value={value}
                                variant='outlined'
                                size='md'
                                color='primary'
                                sx={{ fontSize: 'md' }}
                                checked={appStateData.isStaging === value}
                              />
                              <Typography fontSize={'md'} fontWeight={'sm'}>
                                {label}
                              </Typography>
                            </MenuItem>
                          );
                        })}
                      </FormControl>
                    ) : null}
                    <Divider />
                    <MenuItem onClick={handleFollowingClick}>
                      <StyledLink
                        to={`${FOLLOWING_PROJECTS_ON_SEARCH_PAGE}${filterState?.searchParams.has('view') ? `&view=${filterState?.searchParams.get('view')}` : ''}`}
                        sx={{ height: (theme) => theme.spacing(4) }}
                      >
                        <Button
                          size='md'
                          variant='plain'
                          color='primary'
                          startDecorator={<FiPlusCircle size={20} />}
                          sx={{ padding: 0 }}
                        >
                          <Typography fontSize={'md'} fontWeight={'sm'}>
                            Following ({isLoading ? '...' : followingProjectsCount})
                          </Typography>
                        </Button>
                      </StyledLink>
                    </MenuItem>
                    {profileMenuConfig.map(({ onClick, title, show }) => {
                      if (!show) return null;
                      return (
                        <>
                          <Divider />
                          <MenuItem onClick={onClick}>
                            <Typography fontSize={'md'} fontWeight={'sm'}>
                              {title}
                            </Typography>
                          </MenuItem>
                        </>
                      );
                    })}
                  </Menu>
                </Dropdown>
              </div>
            </>
          )}
          <IconButton
            variant='plain'
            onClick={() => setIsDrawerOpen(true)}
            sx={{ display: { lg: 'none' } }}
          >
            <IoMenu fontSize={20} />
          </IconButton>
        </Stack>
      </Grid>
    </Stack>
  );
};

export { Navbar };
